import {
  Box,
  Button,
  Container,
  Flex,
  Image,
  Typography,
} from "@qws-org/ui-kit";
import type { FC } from "react";
import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import type { Action, Pages, RowTabsData } from "~/core";
import { isExternalLink, isInternalLink } from "~/core/utils/action.utils";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

type RowTabsProps = {
  data: RowTabsData;
  isControlInactive: (control: Action) => boolean;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
};

export const RowTabs: FC<RowTabsProps> = ({
  data,
  isControlInactive,
  getUrl,
  onCallAction,
}: RowTabsProps) => {
  const { items } = data;
  const { getDirectusImage } = useDirectus();
  const isDraggingRef = useRef(false);

  const handleTouchMove = (): void => {
    isDraggingRef.current = true;
  };

  const handleTouchEnd = (): void => {
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 100);
  };

  return (
    <Container p={{ py: 8 }}>
      <Swiper
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        grabCursor
        slidesPerView="auto"
        className="!m-0"
        slidesOffsetBefore={16}
        breakpoints={{
          768: {
            slidesOffsetBefore: 24,
          },
          1024: {
            slidesOffsetBefore: 32,
          },
        }}
        style={{
          paddingRight: "8px",
        }}
      >
        {items?.map((item, index) => {
          const tab = item?.row_tabs_items_id;

          const mobileImage = tab?.icon?.image;
          const mobileWidth = tab?.icon?.mobile_image_width ?? undefined;
          const mobileHeight = tab?.icon?.mobile_image_height ?? undefined;

          const action = tab?.action;
          const controlAction = action?.action?.[0];
          const isButtonInactive = action ? isControlInactive(action) : true;

          let href;

          if (isExternalLink(controlAction?.item)) {
            href = controlAction.item.href ?? undefined;
          }
          if (isInternalLink(controlAction?.item)) {
            href = controlAction.item.page
              ? getUrl(controlAction.item.page)
              : undefined;
          }
          const handlePressButton = (): void => {
            if (isDraggingRef.current) {
              isDraggingRef.current = false;
              return;
            }
            if (action && !isButtonInactive) {
              onCallAction(action);
            }
          };

          return (
            <SwiperSlide
              key={index}
              style={{
                width: `auto`,
              }}
              className="mr-0.5"
            >
              <Box
                key={index}
                width={"min-content"}
                border={{ radius: "40px" }}
                overflow={"hidden"}
                style={{ flexShrink: 0 }}
              >
                {tab?.action_text && (
                  <Button
                    p={{ px: 12, py: 8 }}
                    variant="tertiary"
                    size="xsmall"
                    fontWeight="mobile.bold"
                    width={"max-content"}
                    bg={"surface-neutral-default"}
                    href={!isButtonInactive ? href : undefined}
                    onPress={handlePressButton}
                  >
                    <Flex flexDirection="row" align="center" gap={8}>
                      {mobileImage?.id && (
                        <Image
                          value={getDirectusImage(mobileImage.id, {
                            width: mobileWidth,
                            height: mobileHeight,
                            format: "webp",
                          })}
                          overflow="hidden"
                          image={{
                            alt: tab?.icon?.alternative_text ?? "",
                          }}
                          width={mobileWidth}
                          height={mobileHeight}
                          minWidth="fit-content"
                        />
                      )}
                      <Typography
                        size={"medium"}
                        md={{
                          fontSize: "body.desktop.medium",
                        }}
                        d={{
                          fontSize: "body.desktop.medium",
                        }}
                        fontWeight={"mobile.normal"}
                      >
                        {tab?.action_text}
                      </Typography>
                    </Flex>
                  </Button>
                )}
              </Box>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Container>
  );
};
